import { useEffect, useRef } from 'react';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';

type UseMarkConversationReadOnMobileBackNavArgs = {
  mobileView: InboxPageMobileView;
  isDesktopView: boolean;
  preventMarkAsRead: boolean;
  hasUnreadMessage: boolean;
  lastMessageDateMs: string | undefined;
  markConversationAsRead: (lastReadMs: string) => void;
  clearPreventMarkAsRead: () => void;
};

/**
 * Mobile mark-read coordinator for the inbox message-list view, driven by `mobileView` transitions.
 *
 * Leaving the message list (tapping back to the conversation list, or opening the renter profile)
 * should clear the unread dot. `MessageList`'s `handleScrollComplete` can't cover this: short
 * conversations may never scroll, and once `mobileView` flips away from `MessageList` its own guard
 * bails. So on the transition away from `MessageList` (while still unread, and not suppressed) this
 * hook marks the conversation read.
 *
 * It also clears `preventMarkAsRead` on the transition *into* `MessageList`. The flag is set when a
 * user explicitly marks the conversation unread, and is otherwise only reset when `conversationId`
 * changes. On mobile, going list -> conversation -> list -> same conversation never changes
 * `conversationId` (the slug is preserved), so without this the flag would stay stuck and the
 * conversation could never be re-marked read after a mark-unread. Re-opening the conversation means
 * the user is reading it again, so the earlier "keep unread" intent no longer applies.
 *
 * The ref updates on every effect run, so each transition condition is true for exactly one run.
 * The `hasUnreadMessage` guard keeps the "scroll-to-end then tap back" path from firing twice, and
 * `preventMarkAsRead` keeps a just-made "mark unread" from being undone on the immediate back-nav.
 * Desktop is excluded so the scroll-to-end trigger stays the only mark-read path there.
 */
export function useMarkConversationReadOnMobileBackNav({
  mobileView,
  isDesktopView,
  preventMarkAsRead,
  hasUnreadMessage,
  lastMessageDateMs,
  markConversationAsRead,
  clearPreventMarkAsRead,
}: UseMarkConversationReadOnMobileBackNavArgs): void {
  const prevMobileViewRef = useRef(mobileView);

  useEffect(() => {
    const prevMobileView = prevMobileViewRef.current;
    prevMobileViewRef.current = mobileView;

    if (isDesktopView) return;

    const isEnteringMessageList =
      prevMobileView !== InboxPageMobileView.MessageList && mobileView === InboxPageMobileView.MessageList;
    if (isEnteringMessageList) {
      clearPreventMarkAsRead();
      return;
    }

    const isLeavingMessageList =
      prevMobileView === InboxPageMobileView.MessageList && mobileView !== InboxPageMobileView.MessageList;
    if (!isLeavingMessageList) return;

    if (preventMarkAsRead || !hasUnreadMessage || lastMessageDateMs === undefined) return;

    markConversationAsRead(lastMessageDateMs);
  }, [
    mobileView,
    isDesktopView,
    preventMarkAsRead,
    hasUnreadMessage,
    lastMessageDateMs,
    markConversationAsRead,
    clearPreventMarkAsRead,
  ]);
}
